/**
 * Coherus Survey - Client-side Interactivity
 *
 * Handles auto-save, form validation, and UI interactions.
 */

// Import CSS for Webpack bundling
import '../css/app.css';

(function() {
    'use strict';

    // Debounce utility
    function debounce(fn, delay) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), delay);
        };
    }

    // Loading spinner state
    let spinnerTimeout = null;
    let spinnerShownAt = 0;
    let pendingRequests = 0;
    const MIN_SPINNER_TIME = 1000;

    // Show loading spinner
    function showSpinner() {
        pendingRequests++;
        if (pendingRequests === 1) {
            const spinner = document.getElementById('loadingSpinner');
            if (!spinner) return;

            spinnerShownAt = Date.now();
            spinner.classList.remove('opacity-0');
            spinner.classList.add('opacity-100');
        }
    }

    // Hide loading spinner (respects minimum display time)
    function hideSpinner() {
        pendingRequests = Math.max(0, pendingRequests - 1);
        if (pendingRequests === 0) {
            const spinner = document.getElementById('loadingSpinner');
            if (!spinner) return;

            const elapsed = Date.now() - spinnerShownAt;
            const remaining = Math.max(0, MIN_SPINNER_TIME - elapsed);

            clearTimeout(spinnerTimeout);
            spinnerTimeout = setTimeout(() => {
                if (pendingRequests === 0) {
                    spinner.classList.remove('opacity-100');
                    spinner.classList.add('opacity-0');
                }
            }, remaining);
        }
    }

    // Update question answered state (for checkmark display)
    function updateAnsweredState(questionId, hasValue) {
        const questionBlock = document.querySelector(`[data-question-id="${questionId}"]`);
        if (questionBlock) {
            if (hasValue) {
                questionBlock.classList.add('is-answered');
                questionBlock.classList.remove('validation-error');
            } else {
                questionBlock.classList.remove('is-answered');
            }
        }
    }

    // Save a single response via AJAX
    async function saveResponse(questionId, value) {
        showSpinner();
        try {
            const response = await fetch('/api/response', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    question_id: questionId,
                    value: value
                })
            });

            const data = await response.json();
            hideSpinner();

            // Update checkmark state
            const hasValue = value !== null && value !== '' && value !== undefined;
            updateAnsweredState(questionId, hasValue);

            return data;
        } catch (error) {
            console.error('Save error:', error);
            hideSpinner();
            return { success: false, error: error.message };
        }
    }

    // Get value from input based on type
    function getInputValue(input) {
        if (input.type === 'radio') {
            const form = input.closest('form');
            const checked = form.querySelector(`input[name="${input.name}"]:checked`);
            if (!checked) return null;

            // Check if it's a select with "other" option
            if (checked.hasAttribute('data-has-other')) {
                const otherInput = form.querySelector(`input[name="${input.name}_other"]`);
                return {
                    selected: checked.value,
                    other_text: otherInput ? otherInput.value : null
                };
            }

            return checked.value;
        }

        if (input.tagName === 'TEXTAREA' || input.type === 'text') {
            // Check if this is a sub-field (comment, other text)
            const parent = input.dataset.parent;
            if (parent) {
                // This is handled by the parent radio/select
                return null;
            }
            return input.value.trim();
        }

        return input.value;
    }

    // Handle "other" option in single select (using event delegation)
    function setupOtherOptionHandlers() {
        const form = document.getElementById('surveyForm');
        if (!form) return;

        form.addEventListener('change', (e) => {
            const radio = e.target;
            if (radio.type !== 'radio') return;

            // Check if this radio group has an "other" option
            const otherInput = form.querySelector(`input[name="${radio.name}_other"]`);
            if (!otherInput) return;

            // Check if the selected radio is the "other" option
            if (radio.hasAttribute('data-has-other')) {
                otherInput.disabled = false;
                setTimeout(() => otherInput.focus(), 10);
            } else {
                otherInput.disabled = true;
                otherInput.value = '';
            }
        });
    }

    // Update character count
    function updateCharCount(textarea) {
        const counter = textarea.parentElement.querySelector('.char-count');
        if (counter) {
            counter.textContent = textarea.value.length;
        }
    }

    // Initialize auto-save for all inputs
    function initAutoSave() {
        const form = document.getElementById('surveyForm');
        if (!form) return;

        // Debounced save for text inputs
        const debouncedSave = debounce((input) => {
            const questionId = input.name;
            const value = getInputValue(input);

            if (value !== null) {
                saveResponse(questionId, value);
            }
        }, 500);

        // Set up "other" option handling (event delegation)
        setupOtherOptionHandlers();

        // Handle radio button toggle behavior via label clicks
        form.querySelectorAll('label:has(input[type="radio"][data-autosave])').forEach(label => {
            const input = label.querySelector('input[type="radio"]');
            if (!input) return;

            // Capture state BEFORE the click changes anything
            label.addEventListener('mousedown', (e) => {
                if (e.target.type === 'text') return;
                input.dataset.wasChecked = input.checked ? 'true' : 'false';
            });

            label.addEventListener('click', (e) => {
                // Don't interfere with "other" text input clicks
                if (e.target.type === 'text') return;

                const questionId = input.name;
                const wasChecked = input.dataset.wasChecked === 'true';

                // Use setTimeout to run after the browser's default radio behavior
                setTimeout(() => {
                    if (wasChecked) {
                        // Was already checked, so uncheck it (toggle off)
                        input.checked = false;

                        // Handle "other" input if present
                        const otherInput = form.querySelector(`input[name="${questionId}_other"]`);
                        if (otherInput) {
                            otherInput.disabled = true;
                            otherInput.value = '';
                        }

                        // Save null to delete the response
                        saveResponse(questionId, null);
                    } else {
                        // Newly checked, save the value
                        const value = getInputValue(input);
                        if (value !== null) {
                            saveResponse(questionId, value);
                        }
                    }
                }, 0);
            });
        });

        // Handle text inputs
        form.querySelectorAll('[data-autosave]').forEach(input => {
            if (input.tagName === 'TEXTAREA' || input.type === 'text') {
                // Debounced save for text inputs
                input.addEventListener('input', () => {
                    if (input.tagName === 'TEXTAREA') {
                        updateCharCount(input);
                    }
                    debouncedSave(input);
                });

                // Initial character count
                if (input.tagName === 'TEXTAREA') {
                    updateCharCount(input);
                }
            }
        });

        // Handle "other" text inputs that affect parent
        form.querySelectorAll('[data-parent]').forEach(input => {
            input.addEventListener('input', debounce(() => {
                const parentName = input.dataset.parent;
                const parentRadio = form.querySelector(`input[name="${parentName}"]:checked`);
                if (parentRadio) {
                    const value = getInputValue(parentRadio);
                    saveResponse(parentName, value);
                }
            }, 500));
        });
    }

    // Validate required questions
    function validateSection() {
        const requiredQuestions = window.surveyData?.requiredQuestions || [];
        const form = document.getElementById('surveyForm');
        if (!form) return { valid: true, missing: [] };

        const missing = [];

        requiredQuestions.forEach(qid => {
            const questionBlock = form.querySelector(`[data-question-id="${qid}"]`);
            if (!questionBlock) return;

            // Check if any radio in this group is checked
            const checked = form.querySelector(`input[name="${qid}"]:checked`);
            if (!checked) {
                missing.push(qid);
                questionBlock.classList.add('validation-error');
            } else {
                questionBlock.classList.remove('validation-error');
            }
        });

        return { valid: missing.length === 0, missing };
    }

    // Show validation error message
    function showValidationError(result) {
        // Scroll to first missing question
        if (result.missing[0]) {
            const el = document.querySelector(`[data-question-id="${result.missing[0]}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }

    // Intercept navigation clicks
    function setupNavigationValidation() {
        const nextBtn = document.getElementById('nextBtn');
        if (nextBtn) {
            nextBtn.addEventListener('click', (e) => {
                const result = validateSection();
                if (!result.valid) {
                    e.preventDefault();
                    showValidationError(result);
                }
            });
        }
    }

    // Submit survey
    window.submitSurvey = async function() {
        // Validate before submit
        const result = validateSection();
        if (!result.valid) {
            showValidationError(result);
            return;
        }
        const btn = document.getElementById('submitBtn');
        if (!btn) return;

        // Disable button and show loading
        btn.disabled = true;
        btn.innerHTML = `
            <span>Submitting...</span>
            <div class="spinner w-4 h-4"></div>
        `;

        try {
            // Navigate to complete page
            window.location.href = '/complete';
        } catch (error) {
            console.error('Submit error:', error);
            btn.disabled = false;
            btn.innerHTML = `
                <span>Submit Survey</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
            `;
            alert('Failed to submit survey. Please try again.');
        }
    };

    // Initialize on DOM ready
    document.addEventListener('DOMContentLoaded', () => {
        initAutoSave();
        setupNavigationValidation();

        // Focus first input on entry page
        const entryInput = document.getElementById('survey_code');
        if (entryInput) {
            entryInput.focus();
        }

        // Keyboard navigation for survey
        document.addEventListener('keydown', (e) => {
            // Enter to submit on entry page
            if (e.key === 'Enter' && document.activeElement === entryInput) {
                return; // Let form handle it
            }

            // Left/Right arrows for section navigation
            if (e.key === 'ArrowLeft' && e.altKey) {
                const prevBtn = document.querySelector('a[href*="section="]');
                if (prevBtn && prevBtn.textContent.includes('Previous')) {
                    prevBtn.click();
                }
            }

            if (e.key === 'ArrowRight' && e.altKey) {
                const nextBtn = document.getElementById('nextBtn');
                if (nextBtn) {
                    nextBtn.click();
                }
            }
        });
    });

})();
